/**
 * Permissions configuration for role-based access control
 * @format
 */

import { UserRole } from './type';

// Permission constants
export const Permission = {
  // Products
  PRODUCT_READ_ALL: 'product:read:all',
  PRODUCT_READ_PUBLISHED: 'product:read:published',
  PRODUCT_CREATE: 'product:create',
  PRODUCT_UPDATE: 'product:update',
  PRODUCT_DELETE: 'product:delete',
  PRODUCT_PUBLISH: 'product:publish',

  // Categories
  CATEGORY_READ: 'category:read',
  CATEGORY_CREATE: 'category:create',
  CATEGORY_UPDATE: 'category:update',
  CATEGORY_DELETE: 'category:delete',

  // Materials
  MATERIAL_READ: 'material:read',
  MATERIAL_CREATE: 'material:create',
  MATERIAL_UPDATE: 'material:update',
  MATERIAL_DELETE: 'material:delete',

  // Colors
  COLOR_READ: 'color:read',
  COLOR_CREATE: 'color:create',
  COLOR_UPDATE: 'color:update',
  COLOR_DELETE: 'color:delete',

  // Size Guides
  SIZE_GUIDE_READ: 'size-guide:read',
  SIZE_GUIDE_CREATE: 'size-guide:create',
  SIZE_GUIDE_UPDATE: 'size-guide:update',
  SIZE_GUIDE_DELETE: 'size-guide:delete',

  // Sizes
  SIZE_READ: 'size:read',
  SIZE_CREATE: 'size:create',
  SIZE_UPDATE: 'size:update',
  SIZE_DELETE: 'size:delete',

  // Orders
  ORDER_READ: 'order:read',
  ORDER_CREATE: 'order:create',
  ORDER_UPDATE_TO_CONFIRMED: 'order:update:confirmed',
  ORDER_UPDATE_FULFILMENT: 'order:update:fulfilment',
  ORDER_UPDATE_STATUS: 'order:update:status',
  ORDER_VIEW_SHIPPING: 'order:view:shipping',
  ORDER_CREATE_SHIPMENT: 'order:create:shipment',

  // Shipping
  SHIPPING_GET_RATES: 'shipping:get-rates',
  SHIPPING_CREATE_SHIPMENT: 'shipping:create-shipment',

  // Email Campaigns
  CAMPAIGN_VIEW: 'campaign:view',
  CAMPAIGN_CREATE: 'campaign:create',
  CAMPAIGN_SCHEDULE: 'campaign:schedule',
  CAMPAIGN_SEND: 'campaign:send',
  CAMPAIGN_VIEW_ANALYTICS: 'campaign:view:analytics',
  // Gates the TCS-admin campaign SURFACE (PE-2125), which is not the same thing as
  // holding a campaign permission. osprey-api's /email-campaigns sits behind
  // CollectiveOnlyGuard, so a merchant is rejected there no matter what campaign
  // actions watchman grants them — and watchman DOES grant merchants full
  // EMAIL_CAMPAIGN CRUD (prisma/seed.ts COMMERCE_RESOURCES → fullCommerceRW).
  // Gating the page on CAMPAIGN_VIEW would therefore put it in a merchant admin's
  // sidebar and 403 on every request, so this collective-only constant exists to
  // mirror the guard rather than the resource.
  CAMPAIGN_ADMIN_VIEW: 'campaign:admin:view',

  // Newsletter
  NEWSLETTER_VIEW_SUBSCRIBERS: 'newsletter:view:subscribers',
  NEWSLETTER_EXPORT_DATA: 'newsletter:export:data',

  // Metadata
  METADATA_VIEW: 'metadata:view',
  METADATA_CREATE: 'metadata:create',
  METADATA_UPDATE: 'metadata:update',

  //Homepage-Slider
  SLIDER_VIEW: "slider:view",
  SLIDER_UPDATE: "slider:update",
  SLIDER_DELETE:"slider:delete",

  // Blog / CMS
  BLOG_POST_VIEW: 'blog-post:view',
  BLOG_POST_CREATE: 'blog-post:create',
  BLOG_POST_UPDATE: 'blog-post:update',
  BLOG_POST_DELETE: 'blog-post:delete',
  BLOG_POST_PUBLISH: 'blog-post:publish',
  BLOG_CATEGORY_VIEW: 'blog-category:view',
  BLOG_CATEGORY_CREATE: 'blog-category:create',
  BLOG_CATEGORY_UPDATE: 'blog-category:update',
  BLOG_CATEGORY_DELETE: 'blog-category:delete',

  // Pages & policies (PE-2175) — CMS-lite standalone content pages. There is no
  // separate create/update/delete/publish constant: the backend has no per-action
  // permission split for this domain, so one MANAGE constant gates every write.
  CONTENT_PAGE_VIEW: 'content-page:view',
  CONTENT_PAGE_MANAGE: 'content-page:manage',

  // Merchants
  MERCHANT_VIEW: 'merchant:view',
  MERCHANT_CREATE: 'merchant:create',
  MERCHANT_UPDATE: 'merchant:update',
  MERCHANT_DEACTIVATE: 'merchant:deactivate',
  MERCHANT_DELETE: 'merchant:delete',
  // ⚠️ Mirrors watchman MERCHANT.MANAGE, which NO role holds today. watchman's seed gives
  // COLLECTIVE_SUPERADMIN MERCHANT:[CREATE, READ, UPDATE, DEACTIVATE, DELETE] and its
  // PermissionGuard is a literal `chars.includes('M')` test with no superadmin bypass, so
  // osprey-api's two `instagram/platform-credential` routes — the only holders of this gate —
  // 403 for everyone. Declared, and deliberately granted to NOBODY below, so the Instagram
  // credential page renders an honest "no role has this" state instead of a dead form behind
  // a nav entry that promises access. Grant it in watchman's seed to light the feature up.
  MERCHANT_MANAGE: 'merchant:manage',
  // Merchant self-onboarding review (PE-1282, backend PE-2077) — approve/reject
  // applications from the public /onboard/signup + /onboard/contract flow.
  // TCS-staff only: osprey-api's controller carries CollectiveOnlyGuard +
  // MERCHANT_ONBOARDING READ/MANAGE from the JWT `p` claim. watchman already
  // seeds MERCHANT_ONBOARDING:[READ, MANAGE] for COLLECTIVE_SUPERADMIN/ADMIN and
  // MERCHANT_ONBOARDING:[READ] for COLLECTIVE_USER (prisma/seed.ts ROLE_DEFAULTS) —
  // these two constants mirror that split; no seed change needed.
  MERCHANT_ONBOARDING_VIEW: 'merchant-onboarding:view',
  MERCHANT_ONBOARDING_MANAGE: 'merchant-onboarding:manage',
  // Merchant-scoped self-service: view OWN contract terms (PE-2098). Held only by
  // MERCHANT_ADMIN/MERCHANT_USER — COLLECTIVE staff read a merchant's terms from the
  // merchant-detail page instead. Backend gate: GET /merchants/me/contract is
  // merchant-scoped + MERCHANT.READ (osprey-api → watchman); this frontend constant
  // only drives nav/route visibility.
  MERCHANT_CONTRACT_VIEW: 'merchant:contract:view',

  // Users (Watchman) — editing a user's details. Chiefly used to fix a PENDING
  // merchant-admin's email before re-sending the invite. Held by COLLECTIVE_SUPERADMIN,
  // COLLECTIVE_ADMIN and MERCHANT_ADMIN (mirrors watchman USER.UPDATE in seed.ts).
  USER_UPDATE: 'user:update',

  // Audit Logs
  AUDIT_LOG_VIEW: 'audit-log:view',

  // Consent Logs
  CONSENT_LOG_VIEW: 'consent-log:view',

  // Search Analytics
  SEARCH_ANALYTICS_VIEW: 'search-analytics:view',

  // Observability — Microsoft Clarity session recordings (PE-1830).
  // Per-merchant scoped: a holder sees recordings for the active merchant only.
  OBSERVABILITY_SESSION_RECORDINGS_VIEW: 'observability:session-recordings:view',

  // Observability — Sentry error/performance monitoring (PE-2128).
  // TCS-admin-only: granted to COLLECTIVE_SUPERADMIN + COLLECTIVE_ADMIN, never
  // merchant roles. Org-level (not per-merchant). Needs the matching watchman
  // seed entry to reach real JWTs (guards read the token, not this map).
  OBSERVABILITY_ERROR_MONITORING_VIEW: 'observability:error-monitoring:view',

  // Contract templates — TCS admin upload + Required/Optional field configuration
  // (PE-2145, backend PE-2144). TCS-staff only: granted to COLLECTIVE_SUPERADMIN +
  // COLLECTIVE_ADMIN, never merchant roles. osprey-api enforces the real gate
  // server-side (CollectiveOnlyGuard + MERCHANT_ONBOARDING READ/MANAGE); these two
  // constants only drive nav/route visibility (VIEW) and the manage actions (MANAGE).
  // Legal-vs-TCS edit gating is deferred to the role model in PE-2146/PE-2147.
  CONTRACT_TEMPLATE_VIEW: 'contract-template:view',
  CONTRACT_TEMPLATE_MANAGE: 'contract-template:manage',

  // Platform reporting (PE-2108, backend PE-2091) — TCS-wide merchant/order/finance
  // aggregate. TCS-staff only: granted to COLLECTIVE_SUPERADMIN + COLLECTIVE_ADMIN,
  // never merchant roles. osprey-api enforces the real gate server-side
  // (CollectiveOnlyGuard + REPORTING.READ from the JWT `p` claim); this constant only
  // drives nav/route visibility.
  // watchman already seeds the `REPORTING` resource (migration
  // 20260811000000_add_reporting_resource) and grants REPORTING:[READ] to
  // COLLECTIVE_SUPERADMIN, COLLECTIVE_ADMIN *and* COLLECTIVE_USER — the grants below
  // mirror all three. No seed follow-up needed, unlike PE-2128.
  REPORTING_VIEW: 'reporting:view',

  // Sales pricing & invoice engine (PE-2143, backend PE-2142) — the calculator,
  // price book, sales invoices and pricing assumptions. TCS-staff only, same shape
  // as REPORTING above: osprey-api enforces CollectiveOnlyGuard + SALES_PRICING from
  // the JWT `p` claim; VIEW drives nav/route, MANAGE drives the write actions.
  // watchman already seeds the `SALES_PRICING` resource (migration
  // 20260805000000_add_sales_pricing_resource) with [READ, MANAGE] for
  // COLLECTIVE_SUPERADMIN and COLLECTIVE_ADMIN only — deliberately NOT COLLECTIVE_USER,
  // which is why the grants below stop at the two admin roles.
  SALES_PRICING_VIEW: 'sales-pricing:view',
  SALES_PRICING_MANAGE: 'sales-pricing:manage',

  // CRM-lite sales contacts (PE-2159) — prospects, conversations and the merchants they
  // became. TCS-staff only, exactly the same shape as SALES_PRICING above: osprey-api
  // enforces CollectiveOnlyGuard + SALES_CONTACT from the JWT `p` claim; VIEW drives
  // nav/route, MANAGE drives create/update/add-note. watchman already seeds SALES_CONTACT:
  // [READ, MANAGE] for COLLECTIVE_SUPERADMIN and COLLECTIVE_ADMIN only — deliberately NOT
  // COLLECTIVE_USER — which is why the grants below stop at the two admin roles.
  SALES_CONTACT_VIEW: 'sales-contact:view',
  SALES_CONTACT_MANAGE: 'sales-contact:manage',

  // Outreach & messaging kit (PE-2188, backend PE-2187) and the referral program (PE-2190,
  // backend PE-2189). Same shape as SALES_CONTACT: osprey-api enforces the watchman
  // SALES_OUTREACH / REFERRAL resources from the JWT `p` claim — READ drives nav/route, MANAGE
  // every write. watchman seeds both as [READ, MANAGE] for COLLECTIVE_SUPERADMIN and
  // COLLECTIVE_ADMIN only (watchman #119/#120), so the grants below stop at those two roles.
  SALES_OUTREACH_VIEW: 'sales-outreach:view',
  SALES_OUTREACH_MANAGE: 'sales-outreach:manage',
  REFERRAL_VIEW: 'referral:view',
  REFERRAL_MANAGE: 'referral:manage',

  // Monthly Intelligence Report (PE-2141, backend PE-2140) — AI-drafted customer
  // behaviour reports with a TCS review-and-release gate.
  //
  // ⚠️ COLLECTIVE-ONLY SURFACE, and NOT for the reason the resource permission
  // suggests. watchman DOES grant INTELLIGENCE_REPORT:[READ] to MERCHANT_ADMIN
  // (prisma/seed.ts ROLE_DEFAULTS), but osprey-api's controller carries
  // CollectiveOnlyGuard, so a merchant is rejected there whatever watchman
  // granted — verified live on development: a MERCHANT_ADMIN whose JWT `p` claim
  // reads INTELLIGENCE_REPORT:"R" still gets 403 "COLLECTIVE-scoped (TCS staff)
  // access required". Gating on the resource permission would therefore put a
  // page in a merchant admin's sidebar that 403s on every request — exactly the
  // trap CAMPAIGN_ADMIN_VIEW above exists to avoid. Hence a collective-only
  // frontend constant that mirrors the GUARD, not the resource.
  //
  // The merchant-facing half of the ticket is NOT deliverable in Osprey at all:
  // the only merchant-readable route lives on rack-api, and Osprey talks solely
  // to osprey-api (shared/utils/apiBase.ts). It needs a merchant-scoped osprey-api
  // route (à la GET /merchants/me/contract) before it can be built here.
  //
  // VIEW drives nav/route; GENERATE mirrors INTELLIGENCE_REPORT.CREATE (manual
  // generate/regenerate); MANAGE mirrors INTELLIGENCE_REPORT.MANAGE (approve,
  // release, analytics config). watchman seeds the resource already (migration
  // 20260815130000_add_intelligence_report_resource) with [CREATE, READ, MANAGE]
  // for COLLECTIVE_SUPERADMIN + COLLECTIVE_ADMIN and [READ] for COLLECTIVE_USER,
  // so the grants below mirror that split exactly — no seed follow-up needed.
  INTELLIGENCE_REPORT_VIEW: 'intelligence-report:view',
  INTELLIGENCE_REPORT_GENERATE: 'intelligence-report:generate',
  INTELLIGENCE_REPORT_MANAGE: 'intelligence-report:manage',

  // API Keys
  API_KEY_VIEW: 'api-key:view',
  API_KEY_CREATE: 'api-key:create',
  API_KEY_UPDATE: 'api-key:update',
  API_KEY_REVOKE: 'api-key:revoke',

  // Account security — the caller's OWN two-factor settings (PE-2157).
  //
  // Granted to EVERY role on purpose. watchman guards /2fa/enroll|confirm|disable
  // with the session alone (two-factor.controller.ts is @Controller() with no
  // @RequiresPermission), so there is no resource permission to mirror — this
  // constant exists only because the sidebar filter requires one, and hiding the
  // page behind any real permission would lock some roles out of their own
  // account security. Note DASHBOARD_VIEW could NOT stand in: the TENANT_* roles
  // do not hold it.
  SECURITY_SELF_MANAGE: 'security:self:manage',
  // Feature flags (PE-2161, backend PE-2160). TCS-staff only: osprey-api's
  // controller carries CollectiveOnlyGuard on top of FEATURE_FLAG.READ/MANAGE.
  // Unlike CAMPAIGN_ADMIN_VIEW and INTELLIGENCE_REPORT_VIEW above, the guard and
  // the watchman grant AGREE here — prisma/seed.ts gives FEATURE_FLAG:[READ,
  // MANAGE] to COLLECTIVE_SUPERADMIN and COLLECTIVE_ADMIN and to nobody else
  // (not even COLLECTIVE_USER) — so these mirror the resource directly and no
  // collective-only shim constant is needed.
  FEATURE_FLAG_VIEW: 'feature-flag:view',
  FEATURE_FLAG_MANAGE: 'feature-flag:manage',
  // Departments, department roles & staff assignments (PE-2147, backend
  // PE-2146). Mirrors watchman's DEPARTMENT resource: prisma/seed.ts grants
  // [CREATE, READ, UPDATE, DELETE, MANAGE] to COLLECTIVE_SUPERADMIN and
  // COLLECTIVE_ADMIN only. Department-role grants (the JWT `p` claim) are read
  // separately via shared/hooks/useClaimPermission.
  DEPARTMENT_VIEW: 'department:view',
  DEPARTMENT_MANAGE: 'department:manage',

  // Dashboard
  DASHBOARD_VIEW: 'dashboard:view',

  // Invoices (Osprey staff only — COLLECTIVE_SUPERADMIN + COLLECTIVE_ADMIN)
  INVOICE_VIEW: 'invoice:view',
  INVOICE_CREATE: 'invoice:create',
  INVOICE_UPDATE: 'invoice:update',
  INVOICE_VOID: 'invoice:void',

  // Product metadata — attribute schema CRUD (COLLECTIVE_SUPERADMIN only)
  PRODUCT_SCHEMA_MANAGE: 'product-schema:manage',

  // TCS Staff — BPM Officers and Liaisons
  STAFF_VIEW: 'staff:view',
  STAFF_MANAGE: 'staff:manage',

  // Merchant team (PE-2109) — a merchant admin manages their OWN staff & roles.
  // Frontend nav gate only; watchman enforces server-side via USER:[READ/CREATE/
  // UPDATE/DELETE] and scopes every /users call to the caller's own merchant.
  TEAM_VIEW: 'team:view',
  TEAM_MANAGE: 'team:manage',

  // Leads — marketing-site contact submissions, waitlist & discovery briefs.
  // Frontend nav gate only; the synergy BFF enforces access server-side via its
  // role-based CollectiveAdminGuard (COLLECTIVE_ADMIN / COLLECTIVE_SUPERADMIN).
  LEADS_VIEW: 'leads:view',

  // Payments finance (PE-1793) — disputes, reconciliation, settlements, payout
  // destinations and refunds. Server-enforced per-permission in osprey-api via the
  // JWT `p` claim (watchman Resource/Action), mirrored here as the frontend nav gate.
  PAYMENT_VIEW: 'payment:view',
  PAYMENT_DISPUTE_VIEW: 'payment-dispute:view',
  PAYMENT_REFUND: 'payment:refund',
  RECONCILIATION_VIEW: 'reconciliation:view',
  RECONCILIATION_RUN: 'reconciliation:run',
  SETTLEMENT_VIEW: 'settlement:view',
  PAYOUT_DESTINATION_VIEW: 'payout-destination:view',
  PAYOUT_DESTINATION_MANAGE: 'payout-destination:manage',

  // Booking services (PE-1875) — bookable services with the "charge customers"
  // toggle + fee. Frontend nav gate only; osprey-api proxies to booking-service
  // and scopes by the admin's selected merchant (x-tenant-id). Visible to both
  // COLLECTIVE (TCS) admins and MERCHANT admins.
  BOOKING_SERVICE_VIEW: 'booking-service:view',
  BOOKING_SERVICE_MANAGE: 'booking-service:manage',

  // Branches / locations (PE-2139, on the PE-2138 backend). Frontend nav gate only;
  // osprey-api enforces watchman's Resource.BRANCH + Action.READ/CREATE/UPDATE server-side
  // from the JWT `p` claim. Unlike most merchant-scoped features these routes scope by an
  // explicit `merchantId` param rather than x-tenant-id (see features/branches/api).
  // ⚠️ Follow-up: BRANCH is a new watchman resource — confirm it is in watchman's seed
  // (prisma/seed.ts ROLE_DEFAULTS) for these roles, or the UI will 403 despite this gate.
  BRANCH_VIEW: 'branch:view',
  BRANCH_MANAGE: 'branch:manage',

  // Charter (PE-1831) — 5th TCS platform (service/package bookings). Its whole
  // back-office lives in Osprey. Frontend nav gate only; charter-api enforces the
  // real per-resource permissions server-side (SERVICE/PACKAGE/ADDON/BOOKING/ORDER/
  // PORTFOLIO/AVAILABILITY/INTAKE/WAITLIST/MERCHANT) from the JWT `p` claim.
  //   • CHARTER_VIEW   — see the merchant back-office (provider-scoped via x-tenant-id).
  //   • CHARTER_MANAGE — write in the merchant back-office (catalogue + booking/order actions).
  //   • CHARTER_ADMIN  — TCS cross-provider oversight (providers, admin add-ons, read-only bookings/orders/waitlist).
  // ⚠️ Follow-up: add the matching Charter resources/actions to watchman's seed
  // (prisma/seed.ts ROLE_DEFAULTS) — see PR body.
  CHARTER_VIEW: 'charter:view',
  CHARTER_MANAGE: 'charter:manage',
  CHARTER_ADMIN: 'charter:admin',

  // Portfolio (PE-1831 follow-up) — the merchant portfolio gallery, unified across all three
  // solutions that ship one (charter/atelier/rack-and-box) into a single Osprey page
  // (osprey/src/features/portfolio). Frontend nav gate only; each solution's own BFF enforces
  // the real PORTFOLIO.READ/MANAGE permission server-side from the JWT `p` claim (watchman
  // Resource.PORTFOLIO — already seeded in watchman prisma/seed.ts ROLE_DEFAULTS for every
  // role that holds CHARTER_VIEW/CHARTER_MANAGE below, so this mirrors that exact set).
  PORTFOLIO_VIEW: 'portfolio:view',
  PORTFOLIO_MANAGE: 'portfolio:manage',

  // Tenants (tenant model P1) — brand-website tenants over the shared tenant
  // (merchants) table. Mirrors watchman Resource.TENANT actions in seed.ts:
  // view=READ, create=CREATE, update=UPDATE, deactivate=DEACTIVATE, delete=DELETE.
  TENANT_VIEW: 'tenant:view',
  TENANT_CREATE: 'tenant:create',
  TENANT_UPDATE: 'tenant:update',
  TENANT_DEACTIVATE: 'tenant:deactivate',
  TENANT_DELETE: 'tenant:delete',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

// Shared read-only base (no merchant or metadata access)
const COLLECTIVE_USER_PERMISSIONS: Permission[] = [
  Permission.PRODUCT_READ_ALL,
  Permission.CATEGORY_READ,
  Permission.MATERIAL_READ,
  Permission.COLOR_READ,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_READ,
  Permission.ORDER_READ,
  Permission.ORDER_VIEW_SHIPPING,
  Permission.BLOG_POST_VIEW,
  Permission.BLOG_CATEGORY_VIEW,
  Permission.CONTENT_PAGE_VIEW,
  // TCS-admin campaign surface (PE-2125). Collective-only by design — see the
  // CAMPAIGN_ADMIN_VIEW note above; the merchant roles deliberately do NOT get it.
  Permission.CAMPAIGN_ADMIN_VIEW,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.AUDIT_LOG_VIEW,
  Permission.CONSENT_LOG_VIEW,
  Permission.SEARCH_ANALYTICS_VIEW,
  Permission.API_KEY_VIEW,
  Permission.DASHBOARD_VIEW,
  // Platform reporting is read-only and watchman seeds REPORTING:[READ] for
  // COLLECTIVE_USER too (prisma/seed.ts ROLE_DEFAULTS), so mirror it here —
  // withholding it would hide a page the backend actually allows. Note this role
  // does NOT get SALES_PRICING: watchman grants that to SUPERADMIN/ADMIN only.
  Permission.REPORTING_VIEW,
  // Intelligence reports: watchman seeds INTELLIGENCE_REPORT:[READ] for
  // COLLECTIVE_USER, so it gets the read surface but neither GENERATE nor MANAGE.
  Permission.INTELLIGENCE_REPORT_VIEW,
  // Merchant applications: watchman seeds MERCHANT_ONBOARDING:[READ] for
  // COLLECTIVE_USER — read-only, no approve/reject (MANAGE withheld).
  Permission.MERCHANT_ONBOARDING_VIEW,
  // TENANT_VIEW granted wherever MERCHANT_VIEW/merchant read is granted
  // (mirrors watchman: TENANT.READ alongside MERCHANT.READ).
  Permission.TENANT_VIEW,
  // Account security: every signed-in account manages its own 2FA.
  Permission.SECURITY_SELF_MANAGE,
];

// Merchant read-only (no merchant nav, no metadata)
const MERCHANT_READ_ONLY_PERMISSIONS: Permission[] = [
  Permission.PRODUCT_READ_ALL,
  Permission.CATEGORY_READ,
  Permission.MATERIAL_READ,
  Permission.COLOR_READ,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_READ,
  Permission.ORDER_READ,
  Permission.ORDER_VIEW_SHIPPING,
  Permission.BLOG_POST_VIEW,
  Permission.BLOG_CATEGORY_VIEW,
  Permission.CONTENT_PAGE_VIEW,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.DASHBOARD_VIEW,
  // Charter (PE-1831) — read-only visibility into the provider's Charter back-office.
  Permission.CHARTER_VIEW,
  // Portfolio (PE-1831 follow-up) — read-only visibility into the provider's portfolio.
  Permission.PORTFOLIO_VIEW,
  // View own contract terms (PE-2098) — read-only; backend GET /merchants/me/contract.
  Permission.MERCHANT_CONTRACT_VIEW,
  // Earnings/payouts (PE-2107) — read-only; backend GET /merchants/me/earnings,
  // GET /merchants/me/payouts. MerchantPayoutDestination is explicitly documented as
  // "rendered on the merchant-facing Payouts dashboard", so a read-only merchant
  // role needs this to see its own earnings summary and settlement history.
  Permission.SETTLEMENT_VIEW,
  // Account security: every signed-in account manages its own 2FA.
  Permission.SECURITY_SELF_MANAGE,
];

// Osprey admin full permissions (includes merchant management + metadata)
const COLLECTIVE_ADMIN_PERMISSIONS: Permission[] = [
  Permission.MERCHANT_VIEW,
  Permission.MERCHANT_UPDATE,
  Permission.MERCHANT_DEACTIVATE,
  Permission.USER_UPDATE,
  Permission.PRODUCT_READ_ALL,
  Permission.PRODUCT_CREATE,
  Permission.PRODUCT_UPDATE,
  Permission.PRODUCT_DELETE,
  Permission.PRODUCT_PUBLISH,
  Permission.CATEGORY_READ,
  Permission.CATEGORY_CREATE,
  Permission.CATEGORY_UPDATE,
  Permission.CATEGORY_DELETE,
  Permission.MATERIAL_READ,
  Permission.MATERIAL_CREATE,
  Permission.MATERIAL_UPDATE,
  Permission.MATERIAL_DELETE,
  Permission.COLOR_READ,
  Permission.COLOR_CREATE,
  Permission.COLOR_UPDATE,
  Permission.COLOR_DELETE,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_GUIDE_CREATE,
  Permission.SIZE_GUIDE_UPDATE,
  Permission.SIZE_GUIDE_DELETE,
  Permission.SIZE_READ,
  Permission.SIZE_CREATE,
  Permission.SIZE_UPDATE,
  Permission.SIZE_DELETE,
  Permission.ORDER_READ,
  Permission.ORDER_CREATE,
  Permission.ORDER_UPDATE_TO_CONFIRMED,
  Permission.ORDER_UPDATE_FULFILMENT,
  Permission.ORDER_UPDATE_STATUS,
  Permission.ORDER_VIEW_SHIPPING,
  // TCS-admin campaign surface (PE-2125). Collective-only by design — see the
  // CAMPAIGN_ADMIN_VIEW note above; the merchant roles deliberately do NOT get it.
  Permission.CAMPAIGN_ADMIN_VIEW,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_CREATE,
  Permission.CAMPAIGN_SCHEDULE,
  Permission.CAMPAIGN_SEND,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.NEWSLETTER_EXPORT_DATA,
  Permission.METADATA_VIEW,
  Permission.METADATA_CREATE,
  Permission.METADATA_UPDATE,
  Permission.AUDIT_LOG_VIEW,
  Permission.SEARCH_ANALYTICS_VIEW,
  Permission.OBSERVABILITY_SESSION_RECORDINGS_VIEW,
  Permission.OBSERVABILITY_ERROR_MONITORING_VIEW,
  Permission.CONTRACT_TEMPLATE_VIEW,
  Permission.CONTRACT_TEMPLATE_MANAGE,
  Permission.REPORTING_VIEW,
  Permission.SALES_PRICING_VIEW,
  Permission.SALES_PRICING_MANAGE,
  // CRM-lite contacts (PE-2159), mirroring watchman SALES_CONTACT:[READ, MANAGE].
  Permission.SALES_CONTACT_VIEW,
  Permission.SALES_CONTACT_MANAGE,
  // Outreach kit (PE-2188) + referrals (PE-2190), mirroring watchman
  // SALES_OUTREACH:[READ, MANAGE] and REFERRAL:[READ, MANAGE].
  Permission.SALES_OUTREACH_VIEW,
  Permission.SALES_OUTREACH_MANAGE,
  Permission.REFERRAL_VIEW,
  Permission.REFERRAL_MANAGE,
  // Intelligence reports (PE-2141) — full generate/review/release, mirroring
  // watchman INTELLIGENCE_REPORT:[CREATE, READ, MANAGE] for this role.
  Permission.INTELLIGENCE_REPORT_VIEW,
  Permission.INTELLIGENCE_REPORT_GENERATE,
  Permission.INTELLIGENCE_REPORT_MANAGE,
  // Merchant applications (PE-1282) — full approve/reject, mirroring watchman
  // MERCHANT_ONBOARDING:[READ, MANAGE] for this role.
  Permission.MERCHANT_ONBOARDING_VIEW,
  Permission.MERCHANT_ONBOARDING_MANAGE,
  Permission.API_KEY_VIEW,
  Permission.API_KEY_CREATE,
  Permission.API_KEY_UPDATE,
  Permission.API_KEY_REVOKE,
  Permission.DASHBOARD_VIEW,
  Permission.SLIDER_VIEW,
  Permission.SLIDER_UPDATE,
  Permission.SLIDER_DELETE,
  Permission.BLOG_POST_VIEW,
  Permission.CONTENT_PAGE_VIEW,
  Permission.BLOG_POST_CREATE,
  Permission.BLOG_POST_UPDATE,
  Permission.CONTENT_PAGE_MANAGE,
  Permission.BLOG_POST_DELETE,
  Permission.BLOG_POST_PUBLISH,
  Permission.BLOG_CATEGORY_VIEW,
  Permission.BLOG_CATEGORY_CREATE,
  Permission.BLOG_CATEGORY_UPDATE,
  Permission.BLOG_CATEGORY_DELETE,
  Permission.INVOICE_VIEW,
  Permission.INVOICE_CREATE,
  Permission.INVOICE_UPDATE,
  Permission.INVOICE_VOID,
  Permission.STAFF_VIEW,
  Permission.STAFF_MANAGE,
  Permission.LEADS_VIEW,
  // Payments finance (PE-1793)
  Permission.PAYMENT_VIEW,
  Permission.PAYMENT_DISPUTE_VIEW,
  Permission.PAYMENT_REFUND,
  Permission.RECONCILIATION_VIEW,
  Permission.RECONCILIATION_RUN,
  Permission.SETTLEMENT_VIEW,
  Permission.PAYOUT_DESTINATION_VIEW,
  Permission.PAYOUT_DESTINATION_MANAGE,
  // Booking services (PE-1875)
  Permission.BOOKING_SERVICE_VIEW,
  Permission.BOOKING_SERVICE_MANAGE,
  // Branches (PE-2139) — TCS staff manage locations on a merchant's behalf.
  Permission.BRANCH_VIEW,
  Permission.BRANCH_MANAGE,
  // Charter (PE-1831) — TCS staff manage on providers' behalf + cross-provider oversight.
  Permission.CHARTER_VIEW,
  Permission.CHARTER_MANAGE,
  Permission.CHARTER_ADMIN,
  // Portfolio (PE-1831 follow-up) — TCS staff manage on providers' behalf, any solution.
  Permission.PORTFOLIO_VIEW,
  Permission.PORTFOLIO_MANAGE,
  // Tenants (tenant model P1) — full actions, mirrors watchman
  // COLLECTIVE_ADMIN → TENANT [CREATE, READ, UPDATE, DEACTIVATE, DELETE].
  Permission.TENANT_VIEW,
  Permission.TENANT_CREATE,
  Permission.TENANT_UPDATE,
  Permission.TENANT_DEACTIVATE,
  Permission.TENANT_DELETE,
  // Account security: every signed-in account manages its own 2FA.
  Permission.SECURITY_SELF_MANAGE,
  // Feature flags: watchman seeds FEATURE_FLAG:[READ, MANAGE] to this role.
  Permission.FEATURE_FLAG_VIEW,
  Permission.FEATURE_FLAG_MANAGE,
  // Departments (PE-2147): watchman seeds DEPARTMENT CRUD+MANAGE to this role.
  Permission.DEPARTMENT_VIEW,
  Permission.DEPARTMENT_MANAGE,
];

// Merchant admin full permissions (no merchant management, no metadata)
const MERCHANT_ADMIN_PERMISSIONS: Permission[] = [
  Permission.USER_UPDATE,
  Permission.PRODUCT_READ_ALL,
  Permission.PRODUCT_CREATE,
  Permission.PRODUCT_UPDATE,
  Permission.PRODUCT_DELETE,
  Permission.PRODUCT_PUBLISH,
  Permission.CATEGORY_READ,
  Permission.CATEGORY_CREATE,
  Permission.CATEGORY_UPDATE,
  Permission.CATEGORY_DELETE,
  Permission.MATERIAL_READ,
  Permission.MATERIAL_CREATE,
  Permission.MATERIAL_UPDATE,
  Permission.MATERIAL_DELETE,
  Permission.COLOR_READ,
  Permission.COLOR_CREATE,
  Permission.COLOR_UPDATE,
  Permission.COLOR_DELETE,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_GUIDE_CREATE,
  Permission.SIZE_GUIDE_UPDATE,
  Permission.SIZE_GUIDE_DELETE,
  Permission.SIZE_READ,
  Permission.SIZE_CREATE,
  Permission.SIZE_UPDATE,
  Permission.SIZE_DELETE,
  Permission.ORDER_READ,
  Permission.ORDER_CREATE,
  Permission.ORDER_UPDATE_TO_CONFIRMED,
  Permission.ORDER_UPDATE_FULFILMENT,
  Permission.ORDER_UPDATE_STATUS,
  Permission.ORDER_VIEW_SHIPPING,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_CREATE,
  Permission.CAMPAIGN_SCHEDULE,
  Permission.CAMPAIGN_SEND,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.NEWSLETTER_EXPORT_DATA,
  // Audit logs — mirrors watchman AUDIT:[READ] for MERCHANT_ADMIN; audit-service
  // scopes results server-side to the admin's own merchant.
  Permission.AUDIT_LOG_VIEW,
  Permission.OBSERVABILITY_SESSION_RECORDINGS_VIEW,
  Permission.DASHBOARD_VIEW,
  Permission.SLIDER_VIEW,
  Permission.SLIDER_UPDATE,
  Permission.SLIDER_DELETE,
  Permission.BLOG_POST_VIEW,
  Permission.CONTENT_PAGE_VIEW,
  Permission.BLOG_POST_CREATE,
  Permission.BLOG_POST_UPDATE,
  Permission.CONTENT_PAGE_MANAGE,
  Permission.BLOG_POST_DELETE,
  Permission.BLOG_POST_PUBLISH,
  Permission.BLOG_CATEGORY_VIEW,
  Permission.BLOG_CATEGORY_CREATE,
  Permission.BLOG_CATEGORY_UPDATE,
  Permission.BLOG_CATEGORY_DELETE,
  // Booking services (PE-1875)
  Permission.BOOKING_SERVICE_VIEW,
  Permission.BOOKING_SERVICE_MANAGE,
  // Branches (PE-2139) — merchants manage their own locations.
  Permission.BRANCH_VIEW,
  Permission.BRANCH_MANAGE,
  // Charter (PE-1831) — providers manage their own Charter back-office (provider = merchant).
  Permission.CHARTER_VIEW,
  Permission.CHARTER_MANAGE,
  // Portfolio (PE-1831 follow-up) — merchants manage their own portfolio, any solution.
  Permission.PORTFOLIO_VIEW,
  Permission.PORTFOLIO_MANAGE,
  // View their own one-time invoices and send them to a customer (no create/void — TCS-only).
  // Keep in lockstep with watchman seed: MERCHANT_ADMIN → INVOICE [READ, UPDATE].
  Permission.INVOICE_VIEW,
  Permission.INVOICE_UPDATE,
  // Tenants (tenant model P1) — read-only; mirrors watchman MERCHANT_ADMIN → TENANT [READ].
  Permission.TENANT_VIEW,
  // View own contract terms (PE-2098) — read-only; backend GET /merchants/me/contract.
  Permission.MERCHANT_CONTRACT_VIEW,
  // Earnings/payouts (PE-2107) — read-only; backend GET /merchants/me/earnings,
  // GET /merchants/me/payouts. Gates the merchant-facing Payouts dashboard
  // (earnings summary, payout destination, settlement history).
  Permission.SETTLEMENT_VIEW,
  // Team (PE-2109) — manage own staff & roles; mirrors watchman MERCHANT_ADMIN →
  // USER [READ, CREATE, UPDATE, DELETE], scoped server-side to the own merchant.
  Permission.TEAM_VIEW,
  Permission.TEAM_MANAGE,
  // Account security: every signed-in account manages its own 2FA.
  Permission.SECURITY_SELF_MANAGE,
];

// Tenant admin (tenant model P1) — administers a brand-website tenant.
// Mirrors watchman seed TENANT_ADMIN → TENANT [READ, UPDATE], USER [CRUD],
// ROLE/PERMISSION/AUDIT/CONTACT [READ]. Notes on the mapping:
//  • USER_UPDATE is osprey's only user-management gate (USER CRUD has no other
//    frontend constants — user management UIs check USER_UPDATE).
//  • CONTACT/ROLE/PERMISSION reads have no osprey Permission constants today;
//    they gate nothing in the UI, so nothing to mirror yet.
//  • BOOKING_SERVICE_* is frontend-gate-only vocabulary (no watchman Resource) —
//    granted here per the tenant design so brand sites can manage bookings.
const TENANT_ADMIN_PERMISSIONS: Permission[] = [
  Permission.TENANT_VIEW,
  Permission.TENANT_UPDATE,
  Permission.USER_UPDATE,
  Permission.AUDIT_LOG_VIEW,
  Permission.BOOKING_SERVICE_VIEW,
  Permission.BOOKING_SERVICE_MANAGE,
  // Account security: every signed-in account manages its own 2FA.
  Permission.SECURITY_SELF_MANAGE,
];

// Tenant user (tenant model P1) — read-only within own brand-website tenant.
// Mirrors watchman seed TENANT_USER → TENANT/USER/CONTACT [READ].
const TENANT_USER_PERMISSIONS: Permission[] = [
  Permission.TENANT_VIEW,
  // Account security: every signed-in account manages its own 2FA.
  Permission.SECURITY_SELF_MANAGE,
];

// Role to permissions mapping (Watchman IAM roles)
export const RolePermissions: Record<UserRole, Permission[]> = {
  // Governance read-only + merchant view + metadata + merchant creation
  [UserRole.COLLECTIVE_SUPERADMIN]: [
    Permission.MERCHANT_VIEW,
    Permission.MERCHANT_CREATE,
    Permission.MERCHANT_UPDATE,
    Permission.MERCHANT_DELETE,
    Permission.MERCHANT_DEACTIVATE,
    Permission.USER_UPDATE,
    Permission.PRODUCT_READ_ALL,
    Permission.CATEGORY_READ,
    Permission.MATERIAL_READ,
    Permission.COLOR_READ,
    Permission.SIZE_GUIDE_READ,
    Permission.SIZE_READ,
    Permission.ORDER_READ,
    Permission.ORDER_VIEW_SHIPPING,
    // TCS-admin campaign surface (PE-2125). Collective-only by design — see the
    // CAMPAIGN_ADMIN_VIEW note above; the merchant roles deliberately do NOT get it.
    Permission.CAMPAIGN_ADMIN_VIEW,
    Permission.CAMPAIGN_VIEW,
    Permission.CAMPAIGN_VIEW_ANALYTICS,
    Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
    Permission.METADATA_VIEW,
    Permission.AUDIT_LOG_VIEW,
    Permission.CONSENT_LOG_VIEW,
    Permission.SEARCH_ANALYTICS_VIEW,
    Permission.OBSERVABILITY_SESSION_RECORDINGS_VIEW,
    Permission.OBSERVABILITY_ERROR_MONITORING_VIEW,
    Permission.CONTRACT_TEMPLATE_VIEW,
    Permission.CONTRACT_TEMPLATE_MANAGE,
    Permission.REPORTING_VIEW,
    Permission.SALES_PRICING_VIEW,
    Permission.SALES_PRICING_MANAGE,
    // CRM-lite contacts (PE-2159), mirroring watchman SALES_CONTACT:[READ, MANAGE].
    Permission.SALES_CONTACT_VIEW,
    Permission.SALES_CONTACT_MANAGE,
    // Outreach kit (PE-2188) + referrals (PE-2190), mirroring watchman
    // SALES_OUTREACH:[READ, MANAGE] and REFERRAL:[READ, MANAGE].
    Permission.SALES_OUTREACH_VIEW,
    Permission.SALES_OUTREACH_MANAGE,
    Permission.REFERRAL_VIEW,
    Permission.REFERRAL_MANAGE,
    // Intelligence reports (PE-2141) — full generate/review/release, mirroring
    // watchman INTELLIGENCE_REPORT:[CREATE, READ, MANAGE] for this role.
    Permission.INTELLIGENCE_REPORT_VIEW,
    Permission.INTELLIGENCE_REPORT_GENERATE,
    Permission.INTELLIGENCE_REPORT_MANAGE,
    // Merchant applications (PE-1282) — full approve/reject, mirroring watchman
    // MERCHANT_ONBOARDING:[READ, MANAGE] for this role.
    Permission.MERCHANT_ONBOARDING_VIEW,
    Permission.MERCHANT_ONBOARDING_MANAGE,
    Permission.API_KEY_VIEW,
    Permission.API_KEY_CREATE,
    Permission.API_KEY_REVOKE,
    Permission.DASHBOARD_VIEW,
    Permission.SLIDER_VIEW,
    Permission.BLOG_POST_VIEW,
    Permission.CONTENT_PAGE_VIEW,
    Permission.BLOG_POST_CREATE,
    Permission.BLOG_POST_UPDATE,
    Permission.CONTENT_PAGE_MANAGE,
    Permission.BLOG_POST_DELETE,
    Permission.BLOG_POST_PUBLISH,
    Permission.BLOG_CATEGORY_VIEW,
    Permission.BLOG_CATEGORY_CREATE,
    Permission.BLOG_CATEGORY_UPDATE,
    Permission.BLOG_CATEGORY_DELETE,
    Permission.INVOICE_VIEW,
    Permission.INVOICE_CREATE,
    Permission.INVOICE_UPDATE,
    Permission.INVOICE_VOID,
    Permission.PRODUCT_SCHEMA_MANAGE,
    Permission.STAFF_VIEW,
    Permission.STAFF_MANAGE,
    Permission.LEADS_VIEW,
    // Payments finance (PE-1793)
    Permission.PAYMENT_VIEW,
    Permission.PAYMENT_DISPUTE_VIEW,
    Permission.PAYMENT_REFUND,
    Permission.RECONCILIATION_VIEW,
    Permission.RECONCILIATION_RUN,
    Permission.SETTLEMENT_VIEW,
    Permission.PAYOUT_DESTINATION_VIEW,
    Permission.PAYOUT_DESTINATION_MANAGE,
    // Booking services (PE-1875) — view only for superadmin governance posture
    Permission.BOOKING_SERVICE_VIEW,
    // Charter (PE-1831) — governance visibility + cross-provider oversight.
    Permission.CHARTER_VIEW,
    Permission.CHARTER_MANAGE,
    Permission.CHARTER_ADMIN,
    // Portfolio (PE-1831 follow-up) — governance visibility + cross-provider management.
    Permission.PORTFOLIO_VIEW,
    Permission.PORTFOLIO_MANAGE,
    // Tenants (tenant model P1) — full actions, mirrors watchman
    // COLLECTIVE_SUPERADMIN → TENANT [CREATE, READ, UPDATE, DEACTIVATE, DELETE].
    Permission.TENANT_VIEW,
    Permission.TENANT_CREATE,
    Permission.TENANT_UPDATE,
    Permission.TENANT_DEACTIVATE,
    Permission.TENANT_DELETE,
    // Account security: every signed-in account manages its own 2FA.
    Permission.SECURITY_SELF_MANAGE,
    // Feature flags: watchman seeds FEATURE_FLAG:[READ, MANAGE] to this role.
    Permission.FEATURE_FLAG_VIEW,
    Permission.FEATURE_FLAG_MANAGE,
    // Departments (PE-2147): watchman seeds DEPARTMENT CRUD+MANAGE to this role.
    Permission.DEPARTMENT_VIEW,
    Permission.DEPARTMENT_MANAGE,
  ],

  // Full read/write across all merchants
  [UserRole.COLLECTIVE_ADMIN]: COLLECTIVE_ADMIN_PERMISSIONS,

  // Read-only across products and orders only (no merchants, no metadata)
  [UserRole.COLLECTIVE_USER]: COLLECTIVE_USER_PERMISSIONS,

  // Full read/write within own merchant (no merchant nav, no metadata)
  [UserRole.MERCHANT_ADMIN]: MERCHANT_ADMIN_PERMISSIONS,

  // Read-only within own merchant (no merchant nav, no metadata)
  [UserRole.MERCHANT_USER]: MERCHANT_READ_ONLY_PERMISSIONS,

  // Brand-website tenant admin (tenant model P1)
  [UserRole.TENANT_ADMIN]: TENANT_ADMIN_PERMISSIONS,

  // Read-only within own brand-website tenant (tenant model P1)
  [UserRole.TENANT_USER]: TENANT_USER_PERMISSIONS,
};

/**
 * Get the default route for a given role
 */
export const getDefaultRouteForRole = (role: UserRole | undefined): string => {
  if (!role) return '/login';
  switch (role) {
    case UserRole.COLLECTIVE_SUPERADMIN:
    case UserRole.COLLECTIVE_ADMIN:
    case UserRole.COLLECTIVE_USER:
    case UserRole.MERCHANT_ADMIN:
    case UserRole.MERCHANT_USER:
    case UserRole.TENANT_ADMIN:
    case UserRole.TENANT_USER:
      return '/';
    default:
      return '/no-access';
  }
};

/**
 * Check if a role has a specific permission
 */
export const roleHasPermission = (
  role: UserRole | undefined,
  permission: Permission
): boolean => {
  if (!role) return false;
  const permissions = RolePermissions[role];
  return permissions?.includes(permission) ?? false;
};

/**
 * Check if a role has any of the specified permissions
 */
export const roleHasAnyPermission = (
  role: UserRole | undefined,
  permissions: Permission[]
): boolean => {
  if (!role) return false;
  return permissions.some((p) => roleHasPermission(role, p));
};

/**
 * Check if a role has all of the specified permissions
 */
export const roleHasAllPermissions = (
  role: UserRole | undefined,
  permissions: Permission[]
): boolean => {
  if (!role) return false;
  return permissions.every((p) => roleHasPermission(role, p));
};

/**
 * Check if a role can access draft products
 */
export const roleCanAccessDrafts = (role: UserRole | undefined): boolean => {
  return roleHasPermission(role, Permission.PRODUCT_READ_ALL);
};
